import { useState, useEffect } from 'react';
import { useParams } from '@reach/router';
import {
  useAllowByAdminPermissions,
  useAllowByEmailDomain,
  useAllowByPermission,
  useAllowByQuestionEditPermissions,
} from '../../../../../hooks';
import { useAuth } from '@calefy-inc/authentication';
import { InsufficientPermissionsErrorPage } from '../../../../common/ErrorHandling';
import { useLazyQuery } from '@apollo/client';
import { FINAL_FORM } from '../../../../../queries';
import { ProgramBuilderForm } from '../../../../FormManager/classes';
import LoadingScreen from '../../../components/LoadingScreen';
import { ErrorView } from '../../../../common/ErrorView';
import FormBuilder from '../../../../FormManager/FormBuilder';
import { StoreState } from '../../../../../store';
import { useSelector, useDispatch } from 'react-redux';
import { formStore } from '../../../../../store';
import { useIsPolicyStreamOrganization } from '../../../../PolicyStream/hooks/useIsPolicyStreamOrganization';

const getMatchingFormByInternalNameGenerator =
  (internalName: string) => (state: StoreState) =>
    state.formStore.forms.find((form) => form.internalName === internalName);
export const EditFormByInternalNameManager = () => {
  const { internalName } = useParams();
  const isPolicyStreamOrganization = useIsPolicyStreamOrganization();
  const isAdmin = useAllowByAdminPermissions();
  const { token } = useAuth();
  const hasCorrectDomain = useAllowByEmailDomain('calefy.ca');
  const hasFormEditPermissions = useAllowByPermission('edit:forms');
  const { allowed: hasQuestionEditPermissions } =
    useAllowByQuestionEditPermissions();
  const canOpenForm =
    hasCorrectDomain === true ||
    hasFormEditPermissions === true ||
    hasQuestionEditPermissions === true;
  const [form, setForm] = useState<ProgramBuilderForm>();
  const foundInStore = useSelector(
    getMatchingFormByInternalNameGenerator(internalName),
  );
  const [getForm, { loading, error, data }] = useLazyQuery(FINAL_FORM);
  const dispatch = useDispatch();

  // get the matching form from the backend if we can't find one in the store
  useEffect(() => {
    if (token && canOpenForm && internalName && !foundInStore) {
      getForm({
        variables: {
          token,
          internalName,
        },
      });
    }
  }, [token, canOpenForm, internalName, foundInStore]);

  // set the form regardless of where we got it from
  useEffect(() => {
    if (foundInStore) {
      setForm(foundInStore);
    } else if (data?.finalForm) {
      setForm(ProgramBuilderForm.generateFromBackendResponse(data.finalForm));
    }
  }, [data, foundInStore]);

  // generate our own form if we can't find one
  useEffect(() => {
    if (
      error?.message.match(/finalform matching query does not exist/i) &&
      !foundInStore
    ) {
      dispatch(formStore.actions.setInternalName({ internalName }));
    }
  }, [error, foundInStore]);

  // ensure that the form is in the formstore
  useEffect(() => {
    if (form) {
      dispatch(formStore.actions.loadExistingForms({ loadedForms: [form] }));
    }
  }, [form]);

  if (
    (hasFormEditPermissions === false &&
      hasQuestionEditPermissions === false &&
      hasCorrectDomain !== true) ||
    (isPolicyStreamOrganization && !isAdmin)
  ) {
    return <InsufficientPermissionsErrorPage />;
  }
  if (loading) {
    return <LoadingScreen />;
  }
  if (!form && error) {
    return <ErrorView />;
  }
  if (form) {
    return <FormBuilder form={form} />;
  }
  return null;
};
